import React from "react";
import { FormSectionElements, FormSections, ProgramContainer } from "src/entities/formTemplateDataType";
import StepIndicator from "../subComponent/step-indicator/StepIndicator";
import { styled } from "styled-components";
import { RenewType } from '../../entities/CxpType';

const AlertHeader = styled.div`
    display: flex;
    align-items: center;
`;

const AlertTitle = styled.div`
    display: flex;
    align-items: center;
`;

const SummaryDivider = styled.hr`
    border-top: 2px solid #1A1A1A;
    margin-bottom: 0;
`;

const SummaryTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const SummaryRow = styled.tr`
    border-bottom: 1px solid #C4C4C4;
`;

const SummaryLabelCell = styled.td`
    padding: 1rem 0;
    font-weight: bold;
    width: 45%;
    vertical-align: top;
`;

const SummaryValueCell = styled.td`
    padding: 1rem 0;
    vertical-align: top;
`;

const BackButtonContainer = styled.div`
    margin-top: 2rem;
`;

interface Props {
    programContainer: ProgramContainer;
    isEng: boolean;
    Visible: boolean;
    applicationType?: string, 
    renewType?: string 
    callback?: (p: ProgramContainer) => void;
    backToRequestsUrl?: string;
}

const EhocmAdminConfirmationComponent: React.FC<Props> = ({
    programContainer,
    isEng,
    Visible,
    applicationType,
    renewType,
    backToRequestsUrl,
}) => {
    if (!Visible) return null;

    const elements: FormSectionElements[] = programContainer.ListOfFormSections
        .find(sec => sec.CustomizedReviewPage)?.ListOfFormSubSections?.[0]?.ListOfFormSectionElements || [];

    const find = (name: string) => elements.find(ele => ele.LoadBindName === name);

    const firstNameField = find('firstNameSummary');
    const lastNameField = find('lastNameSummary');
    const emailField = find('emailSummary');
    const phoneField = find('phoneNumberSummary');
    const orgField = find('organizationNameSummary');
    const documentsField = find('uploadedDocumentsSummary');
    const statusField = find('statusSummary');

    const firstName = firstNameField?.Value || '';
    const lastName = lastNameField?.Value || '';
    const status = applicationType || renewType || statusField?.Value || '';
    const isApproved = status?.toLowerCase() !== 'deny';

    const extractFileNames = (value: any): string[] => {
        if (Array.isArray(value)) {
            return value.map((f: any) => typeof f === 'string' ? f : f.fileName).filter(Boolean);
        }
        if (typeof value === 'string') {
            const matches = value.match(/File name:\s*([^<\n]+)/gi) || [];
            return matches.map((m: string) => m.replace(/^File name:\s*/i, '').trim()).filter(Boolean);
        }
        return [];
    };
    const documents = extractFileNames(documentsField?.Value);

    const backToRequestsLabel = isEng ? 'Back to requests' : 'Retour aux demandes';

    const headingText = isEng
        ? `The request has been successfully ${isApproved ? 'approved' : 'denied'}.`
        : `La demande a été ${isApproved ? 'approuvée' : 'refusée'} avec succès.`;

    const alertText = isEng
        ? `${firstName} ${lastName}'s request has been ${isApproved ? 'approved' : 'denied'}.`
        : `La demande de ${firstName} ${lastName} a été ${isApproved ? 'approuvée' : 'refusée'}.`;

    const requestSummaryLabel = isEng ? 'Request summary' : 'Résumé de la demande';

    const summaryRows = [
        { label: isEng ? 'First name' : 'Prénom', value: firstName || null },
        { label: isEng ? 'Last name' : 'Nom de famille', value: lastName || null },
        { label: isEng ? 'Email' : 'Courriel', value: emailField?.Value || null },
        { label: isEng ? 'Phone number' : 'Numéro de téléphone', value: phoneField?.Value || null },
        { label: isEng ? 'Organization name' : "Nom de l'organisme", value: orgField?.Value || null },
        { label: isEng ? 'Uploaded documents' : 'Documents téléversés', value: documents.length > 0 ? documents : null },
    ].filter(row => row.value !== null && row.value !== '');
    const backUrl = '/on-form/admin-portal/review-all-requests'

    const handleBackToRequests = () => {
        window.location.href = backUrl
    };

    return (
        <div>
            <StepIndicator
                withbackbtn={false}
                sectionSequence={-1}
                title={backToRequestsLabel}
                programContainer={programContainer}
                isEng={isEng}
            />

            <a href={backUrl} className="ontario-back-link">
                {backToRequestsLabel}
            </a>

            <h1>{headingText}</h1>

            <div className="ontario-alert ontario-alert--success callout-max-width" role="alert">
                <AlertHeader className="ontario-alert__header">
                    <div className="ontario-alert__header-icon">
                        <svg
                            className="ontario-icon"
                            aria-hidden="true"
                            focusable="false"
                            viewBox="0 0 24 24"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <use href="#ontario-icon-alert-success"></use>
                        </svg>
                    </div>
                    <AlertTitle>
                        <h2 className="ontario-alert__header-title ontario-h4">{alertText}</h2>
                    </AlertTitle>
                </AlertHeader>
            </div>

            <h2>{requestSummaryLabel}</h2>
            <SummaryDivider />

            <SummaryTable>
                <tbody>
                    {summaryRows.map((row, index) => (
                        <SummaryRow key={index}>
                            <SummaryLabelCell>
                                {row.label}
                            </SummaryLabelCell>
                            <SummaryValueCell>
                                {Array.isArray(row.value)
                                    ? row.value.map((doc, i) => <div key={i} dangerouslySetInnerHTML={{__html: doc}} />)
                                    : row.value}
                            </SummaryValueCell>
                        </SummaryRow>
                    ))}
                </tbody>
            </SummaryTable>

            <BackButtonContainer>
                <button
                    type="button"
                    className="ontario-button ontario-button--secondary"
                    onClick={handleBackToRequests}
                >
                    {backToRequestsLabel}
                </button>
            </BackButtonContainer>
        </div>
    );
};

export default EhocmAdminConfirmationComponent;
